import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { isPlanRefOnDay, planRefsEqual } from "../lib/planSlotRefs";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchPlanRange,
  addPlanItem,
  removePlanItem,
  migrateLocalPlan,
} from "../api/weeklyPlan";

const STORAGE_KEY = "albis-weekly-plan";
/** Set once the local plan has been uploaded to Supabase, so we never re-upload. */
const MIGRATED_KEY = "albis-weekly-plan-migrated";
const PLAN_DAYS = 14;
const PRUNE_DAYS = 30;

/** Format date as YYYY-MM-DD */
function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Add days to a date */
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Get 14-day range starting from the given date */
export function getDateRange(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const startDate = new Date(d);
  const endDate = addDays(startDate, PLAN_DAYS - 1);
  const dayDates = Array.from({ length: PLAN_DAYS }, (_, i) => addDays(startDate, i));
  return { startDate, endDate, dayDates };
}

/** Create getSlotKey that returns storage key YYYY-MM-DD-hour for day 0-13 */
function createGetSlotKey(startDate) {
  return (day, hour) => {
    const d = addDays(startDate, day);
    return `${toDateStr(d)}-${hour}`;
  };
}

/** Format date range for display (e.g. "20. Mär – 2. Apr 2025") */
function formatDateRange(date = new Date()) {
  const { startDate, endDate } = getDateRange(date);
  const opts = { day: "numeric", month: "short", year: "numeric" };
  const start = startDate.toLocaleDateString("de-CH", opts);
  const end = endDate.toLocaleDateString("de-CH", opts);
  return `${start} – ${end}`;
}

/** Check if a slot key is in old format (day-hour where day is 0-6) */
function isOldSlotKey(key) {
  if (typeof key !== "string") return false;
  const parts = key.split("-");
  if (parts.length !== 2) return false;
  const [day, hour] = parts.map(Number);
  return Number.isInteger(day) && day >= 0 && day <= 6 && Number.isInteger(hour) && hour >= 0 && hour <= 23;
}

/** Prune slots older than PRUNE_DAYS */
function pruneOldSlots(slots) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - PRUNE_DAYS);
  const cutoffStr = toDateStr(cutoff);
  const pruned = {};
  for (const [key, items] of Object.entries(slots || {})) {
    const datePart = key.split("-").slice(0, 3).join("-");
    if (datePart >= cutoffStr) {
      pruned[key] = items;
    }
  }
  return pruned;
}

const EMPTY_PLAN = { slots: {} };

function loadPlan() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_PLAN };
    const stored = JSON.parse(raw);
    if ("meals" in stored || "kidActivities" in stored) {
      return { ...EMPTY_PLAN };
    }
    if (stored.weekKey != null) {
      return { ...EMPTY_PLAN };
    }
    const slots = stored.slots || {};
    const hasOldKeys = Object.keys(slots).some(isOldSlotKey);
    if (hasOldKeys) {
      return { ...EMPTY_PLAN };
    }
    return { slots: pruneOldSlots(slots) };
  } catch {
    return { ...EMPTY_PLAN };
  }
}

function savePlan(plan) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  } catch (e) {
    console.error("Failed to save weekly plan:", e);
  }
}

/** Remove a ref from a slot key, returning a new plan (used to revert a failed add). */
function withoutRef(plan, key, ref) {
  const items = (plan.slots?.[key] || []).filter((it) => !planRefsEqual(it, ref));
  const slots = { ...(plan.slots || {}) };
  if (items.length === 0) delete slots[key];
  else slots[key] = items;
  return { ...plan, slots };
}

export function useWeeklyPlan() {
  const { user } = useAuth();
  const { startDate, dayDates } = useMemo(() => getDateRange(), []);
  const [plan, setPlan] = useState(loadPlan);

  // Refs let the stable add/remove callbacks read the latest plan and auth state
  // without re-creating themselves (and without relying on setState side effects).
  const planRef = useRef(plan);
  useEffect(() => {
    planRef.current = plan;
  }, [plan]);
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const applyPlan = useCallback((next) => {
    planRef.current = next;
    setPlan(next);
  }, []);

  const getSlotKey = useMemo(() => createGetSlotKey(startDate), [startDate]);

  const reload = useCallback(() => {
    if (userRef.current) {
      fetchPlanRange({ startDate, days: PLAN_DAYS })
        .then((data) => applyPlan(data))
        .catch((e) => console.error("Failed to load weekly plan:", e));
    } else {
      applyPlan(loadPlan());
    }
  }, [startDate, applyPlan]);

  // Load on mount and whenever auth state changes. Signed in: migrate any local
  // plan once, then read from Supabase. Signed out: keep using localStorage.
  useEffect(() => {
    let active = true;
    async function init() {
      if (user) {
        try {
          if (!localStorage.getItem(MIGRATED_KEY)) {
            const local = loadPlan();
            if (Object.keys(local.slots || {}).length > 0) {
              await migrateLocalPlan(local.slots);
            }
            localStorage.setItem(MIGRATED_KEY, "1");
          }
        } catch (e) {
          console.error("Weekly plan migration failed:", e);
        }
        try {
          const data = await fetchPlanRange({ startDate, days: PLAN_DAYS });
          if (active) applyPlan(data);
        } catch (e) {
          console.error("Failed to load weekly plan:", e);
        }
      } else if (active) {
        applyPlan(loadPlan());
      }
    }
    init();
    return () => {
      active = false;
    };
  }, [user, startDate, applyPlan]);

  const addToSlot = useCallback(
    (day, hour, { columnId, eventId }) => {
      const ref = { columnId, eventId };
      const current = planRef.current;
      if (isPlanRefOnDay(current.slots || {}, day, getSlotKey, ref)) {
        return;
      }
      const key = getSlotKey(day, hour);
      const items = [...(current.slots?.[key] || []), ref];
      const next = { ...current, slots: { ...(current.slots || {}), [key]: items } };
      applyPlan(next);

      if (userRef.current) {
        const slotDate = toDateStr(addDays(startDate, day));
        addPlanItem({ slotDate, slotHour: hour, columnId, eventId }).catch((e) => {
          console.error("Failed to add plan item:", e);
          applyPlan(withoutRef(planRef.current, key, ref));
        });
      } else {
        savePlan(next);
      }
    },
    [getSlotKey, startDate, applyPlan]
  );

  const removeFromSlot = useCallback(
    (day, hour, index) => {
      const current = planRef.current;
      const key = getSlotKey(day, hour);
      const existing = current.slots?.[key] || [];
      const ref = existing[index];
      const items = [...existing];
      items.splice(index, 1);
      const slots = { ...(current.slots || {}) };
      if (items.length === 0) delete slots[key];
      else slots[key] = items;
      const next = { ...current, slots };
      applyPlan(next);

      if (userRef.current) {
        if (ref) {
          removePlanItem({
            slotDate: toDateStr(addDays(startDate, day)),
            slotHour: hour,
            columnId: ref.columnId,
            eventId: ref.eventId,
          }).catch((e) => {
            console.error("Failed to remove plan item:", e);
            reload();
          });
        }
      } else {
        savePlan(next);
      }
    },
    [getSlotKey, startDate, applyPlan, reload]
  );

  return {
    plan,
    addToSlot,
    removeFromSlot,
    dayDates,
    getSlotKey,
    dateRangeLabel: formatDateRange(),
  };
}
